import { useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import {
  fetchWebsiteChart,
  todayIn,
  type WebsiteReportChart,
  type WebsiteReportDay,
  type WebsiteReportType,
} from '../lib/websiteVisitsReport';

export type DevWebsiteVisitsDay = WebsiteReportDay;

export interface DevWebsiteVisitsChart extends WebsiteReportChart {
  loading: boolean;
  error: string | null;
}

export type { WebsiteReportType };

const empty: DevWebsiteVisitsChart = {
  visits: 0,
  users: 0,
  conversions: 0,
  conversionRate: null,
  types: [],
  days: [],
  openDay: false,
  loading: true,
  error: null,
};

/** Month-to-date website chart from the website report, through today in the business timezone. */
export function useDevWebsiteVisitsChart(revision = 0) {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timeZone = selectedBusiness?.timezone || 'America/Vancouver';
  const [report, setReport] = useState<DevWebsiteVisitsChart>(empty);

  useEffect(() => {
    if (!businessId) {
      setReport({ ...empty, loading: false });
      return;
    }

    let cancelled = false;
    setReport((current) => ({ ...current, loading: true, error: null }));
    const until = todayIn(timeZone);
    const since = `${until.slice(0, 8)}01`;

    void fetchWebsiteChart(businessId, since, until, timeZone)
      .then((chart) => {
        if (cancelled) return;
        setReport({ ...chart, loading: false, error: null });
      })
      .catch((err: unknown) => {
        console.error('Website visits chart:', err);
        if (cancelled) return;
        setReport({
          ...empty,
          loading: false,
          error: err instanceof Error ? err.message : 'Could not load website report',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [businessId, timeZone, revision]);

  return report;
}
